import { useState } from "react";
import {
  Alert,
  Button,
  Card,
  CopyButton,
  Group,
  NumberInput,
  SegmentedControl,
  Table,
  TagsInput,
  Text,
} from "@mantine/core";
import { IconAlertTriangle, IconCheck, IconCopy } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import {
  CreateQuizzesError,
  useCreateBenchmarkQuizzes,
  type BenchmarkableTaskType,
  type BenchmarkQuiz,
} from "@labeling-tools/api/lebron/react-query-hooks";
import { TASK_TYPE_ROUTE } from "@/config/data";

// n_questions has no default — an admin must state the quiz length every time.
// The last value they used is remembered purely as a convenience.
const N_QUESTIONS_KEY = "oos-benchmark-n-questions";

// Task type IS remembered across visits (unlike n_questions' bar above) —
// an admin issuing a batch of quizzes overwhelmingly stays on one task type
// for the whole session, so re-selecting it every time would be pure friction.
const TASK_TYPE_KEY = "oos-benchmark-task-type";

const TASK_TYPE_OPTIONS: { value: BenchmarkableTaskType; label: string }[] = [
  { value: "OUT_OF_STOCK_CONFIRMATION", label: "Out of Stock Confirmation" },
  { value: "COVERAGE_REVIEW", label: "Coverage Review" },
];

function isBenchmarkableTaskType(
  value: string,
): value is BenchmarkableTaskType {
  return TASK_TYPE_OPTIONS.some((option) => option.value === value);
}

/** Pragmatic shape check — lebron separately verifies the account has signed in. */
export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/** Split a comma/newline/whitespace-separated blob into deduped emails. */
export function parseEmails(raw: string): string[] {
  const seen = new Set<string>();
  for (const part of raw.split(/[\s,;]+/)) {
    const email = part.trim().toLowerCase();
    if (email) seen.add(email);
  }
  return [...seen];
}

// Routes to the play-mode UI matching the quiz's own task type, not always
// "confirmation" — a COVERAGE_REVIEW quiz's tasks are shaped nothing like an
// OOS confirmation task. NOTE: this only gets the reviewer to the right task
// UI. Unlike OosConfirmationContent.tsx (which special-cases
// OUT_OF_STOCK_CONFIRMATION_BENCHMARK to show a score card and a
// finished-quiz state), CoverageTask.tsx has no benchmark-job awareness yet,
// so a coverage-review quiz can be taken but won't show a score at the end.
function quizUrl(jobId: string, taskType: BenchmarkableTaskType): string {
  return `/jobs/${jobId}/${TASK_TYPE_ROUTE[taskType] ?? "confirmation"}`;
}

export const BenchmarkQuizCreateForm = () => {
  const [emails, setEmails] = useState<string[]>([]);
  const [taskType, setTaskType] = useState<BenchmarkableTaskType>(() => {
    const stored = localStorage.getItem(TASK_TYPE_KEY);
    return stored && isBenchmarkableTaskType(stored)
      ? stored
      : "OUT_OF_STOCK_CONFIRMATION";
  });
  const [nQuestions, setNQuestions] = useState<string | number>(
    () => localStorage.getItem(N_QUESTIONS_KEY) ?? "",
  );
  // Deliberately not persisted: defaulting back to 1 each visit beats
  // accidentally re-sending someone five quizzes.
  const [nQuizzes, setNQuizzes] = useState<string | number>(1);
  // Paired with the task type that produced it — `taskType` state can change
  // before these results are cleared, and quizUrl must still route the OLD
  // batch's links correctly.
  const [created, setCreated] = useState<{
    quizzes: BenchmarkQuiz[];
    taskType: BenchmarkableTaskType;
  } | null>(null);
  const { mutate, isPending, error, reset } = useCreateBenchmarkQuizzes();

  const n = Number(nQuestions);
  const nValid = Number.isInteger(n) && n >= 1;
  const q = Number(nQuizzes);
  const qValid = Number.isInteger(q) && q >= 1;
  const totalQuizzes = emails.length * (qValid ? q : 0);
  const invalidEmails = emails.filter((email) => !isValidEmail(email));
  // Server-reported unknowns, narrowed to pills still present — removing or
  // fixing an entry clears its flag without waiting for the next submit.
  const unknownEmails =
    error instanceof CreateQuizzesError
      ? error.unknownEmails.filter((email) => emails.includes(email))
      : [];
  const emailFieldError =
    invalidEmails.length > 0
      ? `Not a valid email: ${invalidEmails.join(", ")}`
      : unknownEmails.length > 0
        ? `No account has signed in with: ${unknownEmails.join(", ")}`
        : undefined;

  const handleSubmit = () => {
    reset();
    setCreated(null);
    mutate(
      // The API creates one quiz per email ENTRY and never dedupes, so
      // "5 quizzes for billy@" is just billy@ appearing 5 times.
      {
        task_type: taskType,
        emails: emails.flatMap((email) => Array(q).fill(email)),
        n_questions: n,
      },
      {
        onSuccess: (quizzes) => {
          localStorage.setItem(N_QUESTIONS_KEY, String(n));
          localStorage.setItem(TASK_TYPE_KEY, taskType);
          setCreated({ quizzes, taskType });
          setEmails([]);
        },
      },
    );
  };

  return (
    <Card withBorder mb="md">
      <Text fw={600} mb="sm">
        Create quizzes
      </Text>

      <SegmentedControl
        mb="md"
        w="fit-content"
        value={taskType}
        onChange={(value) =>
          isBenchmarkableTaskType(value) && setTaskType(value)
        }
        data={TASK_TYPE_OPTIONS}
      />

      <div className="flex flex-row gap-4 items-start">
        <TagsInput
          label="Reviewer emails"
          description="Type or paste — Enter or comma adds; Backspace or ✕ removes"
          placeholder={emails.length === 0 ? "someone@augmodo.com" : ""}
          value={emails}
          // parseEmails re-normalizes the whole list on every change: pasted
          // blobs (any separators) get split, everything lowercased + deduped.
          onChange={(value) => setEmails(parseEmails(value.join("\n")))}
          splitChars={[",", ";", " "]}
          clearable
          error={emailFieldError}
          w={360}
          labelProps={{ style: { textAlign: "left" } }}
          // Pills keep their size; the ✕ only becomes visible on hover.
          classNames={{
            pill: "[&_button]:opacity-0 [&_button]:transition-opacity [&:hover_button]:opacity-100",
          }}
        />
        <NumberInput
          label="Questions per quiz"
          description="Must not exceed the active candidate pool"
          placeholder="e.g. 20"
          value={nQuestions}
          onChange={setNQuestions}
          min={1}
          allowDecimal={false}
          withAsterisk
          w={220}
          labelProps={{ style: { textAlign: "left" } }}
        />
        <NumberInput
          label="Quizzes per reviewer"
          description="Each quiz samples its own questions"
          value={nQuizzes}
          onChange={setNQuizzes}
          min={1}
          allowDecimal={false}
          w={200}
          labelProps={{ style: { textAlign: "left" } }}
        />
      </div>

      <Group mt="md">
        <Button
          onClick={handleSubmit}
          loading={isPending}
          disabled={
            emails.length === 0 ||
            invalidEmails.length > 0 ||
            !nValid ||
            !qValid
          }
        >
          Create {totalQuizzes || ""} quiz{totalQuizzes === 1 ? "" : "zes"}
        </Button>
        {emails.length > 0 && nValid && qValid && (
          <Text c="dimmed" size="sm">
            {q} quiz{q === 1 ? "" : "zes"} × {n} question
            {n === 1 ? "" : "s"} per reviewer
          </Text>
        )}
      </Group>

      {error && (
        <Alert
          mt="md"
          color="red"
          variant="light"
          icon={<IconAlertTriangle size={16} />}
          title="Could not create quizzes"
        >
          {error.message}
        </Alert>
      )}

      {created && (
        <Alert
          mt="md"
          color="green"
          variant="light"
          icon={<IconCheck size={16} />}
          title={`Created ${created.quizzes.length} quiz${created.quizzes.length === 1 ? "" : "zes"}`}
        >
          <Table>
            <Table.Tbody>
              {created.quizzes.map((quiz) => (
                <Table.Tr key={quiz.id}>
                  <Table.Td>
                    <Text size="sm">{quiz.reviewer_email}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Link
                      to={quizUrl(quiz.id, created.taskType)}
                      style={{ textDecoration: "underline" }}
                    >
                      {quiz.id}
                    </Link>
                  </Table.Td>
                  <Table.Td>
                    <CopyButton
                      value={`${window.location.origin}${quizUrl(quiz.id, created.taskType)}`}
                    >
                      {({ copied, copy }) => (
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          leftSection={<IconCopy size={14} />}
                          onClick={copy}
                        >
                          {copied ? "Copied" : "Copy link"}
                        </Button>
                      )}
                    </CopyButton>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Alert>
      )}
    </Card>
  );
};
